<template>
  <vs-popup
    :active.sync="isVisible"
    title="Compartilhar recurso"
    class="share-resource-popup"
    @close="onClose"
  >
    <div class="share-resource-container">
      <!-- Informações do recurso -->
      <div class="resource-info">
        <div class="icon-container">
          <feather-icon
            :icon="getResourceIcon()"
            size="24"
            class="resource-icon"
          />
        </div>
        <div class="resource-details">
          <h4>{{ resourceName }}</h4>
          <small>{{ resourceType }}</small>
        </div>
      </div>

      <!-- Link de compartilhamento -->
      <div class="share-link-section">
        <h5>Link de compartilhamento Público</h5>
        <div class="share-link-container">
          <div class="link-wrapper">
            <a
              v-if="shareLink"
              :href="shareLink"
              target="_blank"
              rel="noopener noreferrer"
              class="share-link"
            >
              {{ shareLink }}
            </a>
            <span v-else class="no-link">Nenhum link disponível</span>
            <vs-button
              v-if="shareLink"
              type="border"
              size="small"
              class="copy-button"
              @click="copyShareLink"
            >
              <feather-icon icon="CopyIcon" size="14" />
            </vs-button>
          </div>
        </div>
      </div>

      <h5>Compartilhar com</h5>
      <!-- Usuários -->
      <div class="share-users-section">
        <div class="users-container">
          <!-- User Select -->
          <select-suggestion
              :key="selectedUser"
              ref="userSelect"
              column="name,email"
              model="User"
              @selected-model="handleUserSelected"
              label="Usuário"
              :appendClearOption="true"
              :clearAfterSelection="true"
              placeholderText="Digite o nome ou e-mail do usuário"
              :itemTextFormat="
                (model) => `${model.name} <br/><div class=\'text-sm\'>${model.email}<div>`
              "/>

          <div class="selected-items-container">
            <div class="selected-items-heading">
              <span>Usuários selecionados</span>
              <small v-if="selectedUsers.length > 0">({{ selectedUsers.length }})</small>
            </div>
            <div class="selected-items-area">
              <div v-if="selectedUsers.length > 0" class="selected-users">
                <div
                  v-for="userObj in selectedUsers"
                  :key="userObj"
                  class="selected-user-tag"
                  :class="{ 'is-owner': isOwner && userObj.id === currentUser.id }"
                >
                  <span>
                    {{ userObj.name }}
                    <br/><span class='text-sm'>{{userObj.email}}</span>
                    <span v-if="isOwner && userObj.id === currentUser.id" class="owner-badge">Dono</span>
                  </span>

                  <feather-icon
                    v-if="!(isOwner && userObj.id === currentUser.id)"
                    icon="TrashIcon"
                    size="16"
                    class="remove-icon"
                    @click="removeUser(userObj)"
                  />
                </div>
              </div>
              <div v-else class="empty-selection">
                Nenhum usuário selecionado
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Grupos -->
      <div class="share-groups-section">
        <div class="groups-container">
          <select-suggestion
              :key="selectedGroup"
              ref="selectedGroup"
              column="name,display_name"
              model="Role"
              @selected-model="handleGroupSelected"
              label="Grupos"
              :appendClearOption="true"
              :clearAfterSelection="true"
              placeholderText="Digite o nome do grupo"
              :itemTextFormat="
                (model) => `${model.display_name}`
              "/>
          <div class="selected-items-container">
            <div class="selected-items-heading">
              <span>Grupos selecionados</span>
              <small v-if="selectedGroups.length > 0">({{ selectedGroups.length }})</small>
            </div>
            <div class="selected-items-area">
              <div v-if="selectedGroups.length > 0" class="selected-groups">
                <div
                  v-for="groupObj in selectedGroups"
                  :key="groupObj"
                  class="selected-group-tag"
                >
                  <span>
                    {{ groupObj.display_name }}
                  </span>

                  <feather-icon
                    icon="TrashIcon"
                    size="16"
                    class="remove-icon"
                    @click="removeGroup(groupObj)"
                  />
                </div>
              </div>
              <div v-else class="empty-selection">
                Nenhum grupo selecionado
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botões de ação -->
    <template #footer>
      <div class="footer-buttons">
        <vs-button type="border" @click="onClose">
          Cancelar
        </vs-button>
        <vs-button @click="shareResource" :disabled="!canShare">
          Compartilhar
        </vs-button>
      </div>
    </template>
  </vs-popup>
</template>


<script>
import SelectSuggestion from '@/components/SelectSuggestion.vue';

export default {
  name: 'ShareResourcePopup',
  components: {
    SelectSuggestion
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    resource: {
      type: Object,
      default: null
    },
    fileExplorerService: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      shareLink: '',
      selectedUsers: [],
      selectedGroups: [],
      sharing: false,
      linkCopied: false,
      sharingInProgress: false
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    resourceName() {
      return this.resource ? this.resource.name : ''
    },
    resourceType() {
      if (!this.resource) return ''

      switch (this.resource.type) {
        case 'directory':
          return 'Pasta'
        case 'drive':
          return 'Drive'
        default:
          return 'Arquivo'
      }
    },
    canShare() {
      return (this.selectedUsers.length > 0 || this.selectedGroups.length > 0) && !this.sharing
    },
    isOwner() {
      return this.resource?.is_owner || false
    },
    currentUser() {
      const userInfoStr = localStorage.getItem('userInfo')
      const userInfoObject = userInfoStr ? JSON.parse(userInfoStr) : null
      return userInfoObject

    }
  },
  methods: {
    onClose() {
      this.resetForm()
      this.isVisible = false
    },
    resetForm() {
      this.shareLink = ''
      this.selectedUsers = []
      this.selectedGroups = []
      this.sharing = false
    },
    getResourceIcon() {
      if (!this.resource) return 'FileIcon'

      switch (this.resource.type) {
        case 'directory':
          return 'FolderIcon'
        case 'drive':
          return 'HardDriveIcon'
        default:
          // Verificar extensão do arquivo
          const extension = this.resource.extension

          if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) {
            return 'ImageIcon'
          } else if (['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'].includes(extension)) {
            return 'VideoIcon'
          } else if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(extension)) {
            return 'MusicIcon'
          } else if (['doc', 'docx', 'odt', 'rtf'].includes(extension)) {
            return 'FileTextIcon'
          } else if (['xls', 'xlsx', 'ods', 'csv'].includes(extension)) {
            return 'FileIcon'
          } else if (['ppt', 'pptx', 'odp'].includes(extension)) {
            return 'FileIcon'
          } else if (['pdf'].includes(extension)) {
            return 'FileIcon'
          } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
            return 'PackageIcon'
          }

          return 'FileIcon'
      }
    },
    copyShareLink() {
      if (!this.shareLink) return;

      // Create a temporary textarea element
      const textarea = document.createElement('textarea');
      textarea.value = this.shareLink;
      document.body.appendChild(textarea);

      // Select and copy the text
      textarea.select();
      document.execCommand('copy');

      // Remove the temporary element
      document.body.removeChild(textarea);

      this.linkCopied = true;

      // Reset after 3 seconds
      setTimeout(() => {
        this.linkCopied = false;
      }, 3000);

      // Notify the user
      this.notifySuccess(this.$vs, 'Link de compartilhamento copiado para a área de transferência.');
    },
    handleUserSelected(user) {
      if (user) {
        // Verificar se o usuário já foi adicionado para evitar duplicatas
        const isDuplicate = this.selectedUsers.some(u => u.id === user.id);
        if (!isDuplicate) {
          this.selectedUsers.push(user);
          this.shareWithUser(user);
        }
      }
    },
    handleGroupSelected(group) {
      if (group) {
        // Verificar se o grupo já foi adicionado para evitar duplicatas
        const isDuplicate = this.selectedGroups.some(g => g.id === group.id);
        if (!isDuplicate) {
          this.selectedGroups.push(group);
          this.shareWithGroup(group);
        }
      }
    },
    getUserById(id) {
      return this.users.find(user => user.id === id) || {}
    },
    getGroupById(id) {
      return this.groups.find(group => group.id === id) || {}
    },
    removeGroup(group) {
      this.selectedGroups = this.selectedGroups.filter(g => g.id !== group.id)
    },
    async shareWithUser(user) {
      if (!this.resource || !user) return;

      try {
        this.sharingInProgress = true;

        const response = await this.fileExplorerService.shareResource({
          resource_id: this.resource.resource_id,
          media_id: this.resource.media_id,
          type: this.resource.type,
          user_id: user.id,
          share_type: 'user'
        });
        this.notifySuccess(this.$vs, `Recurso compartilhado com o usuário ${user.name}`);

      } catch (error) {
        console.error('Erro ao compartilhar com usuário:', error);
        this.notifyError(this.$vs,`Não foi possível compartilhar com o usuário: ${error.message || 'Erro desconhecido'}`);

        // Remover usuário da lista em caso de erro
        this.selectedUsers = this.selectedUsers.filter(u => u.id !== user.id);
      } finally {
        this.sharingInProgress = false;
      }
    },
    async shareWithGroup(group) {
      if (!this.resource || !group) return;

      try {
        this.sharingInProgress = true;

        const response = await this.fileExplorerService.shareResource({
          resource_id: this.resource.resource_id ?? '',
          media_id: this.resource.media_id ?? '',
          type: this.resource.type,
          group_id: group.id,
          share_type: 'group'
        });

        this.notifySuccess(this.$vs, `Recurso compartilhado com o grupo ${group.display_name || group.name}`);

      } catch (error) {
        console.error('Erro ao compartilhar com grupo:', error);
        this.notifyError(this.$vs,`Não foi possível compartilhar com o grupo: ${error.message || 'Erro desconhecido'}`);
        // Remover grupo da lista em caso de erro
        this.selectedGroups = this.selectedGroups.filter(g => g.id !== group.id);
      } finally {
        this.sharingInProgress = false;
      }
    },
    async removeUser(user) {
      if (!this.resource || !user) return;
      // Prevent removing the owner if the current user is the owner
      if (this.isOwner && user.id === this.currentUser.id) {
        this.$vs.notification({
          title: 'Ação não permitida',
          text: 'Você não pode remover a si mesmo como dono do recurso',
          color: 'warning',
          position: 'top-right'
        })
        return
      }

      try {
        this.sharingInProgress = true;

        const response = await this.fileExplorerService.removeResourceSharing({
          resource_id: this.resource.resource_id,
          parent_id: this.resource.parent_id,
          media_id: this.resource.media_id ?? '',
          type: this.resource.type,
          user_id: user.id,
          share_type: 'user'
        });

        this.selectedUsers = this.selectedUsers.filter(u => u.id !== user.id);
        this.notifySuccess(this.$vs, `Acesso removido para o usuário ${user.name}`);

      } catch (error) {
        console.error('Erro ao remover compartilhamento com usuário:', error);
        this.notifyError(this.$vs,`Não foi possível remover o compartilhamento: ${error.message || 'Erro desconhecido'}`);
      } finally {
        this.sharingInProgress = false;
      }
    },
    async removeGroup(group) {
      if (!this.resource || !group) return;

      try {
        this.sharingInProgress = true;

        const response = await this.fileExplorerService.removeResourceSharing({
          resource_id: this.resource.resource_id,
          parent_id: this.resource.parent_id,
          media_id: this.resource.media_id ?? '',
          type: this.resource.type,
          group_id: group.id,
          share_type: 'group'
        });

        this.selectedGroups = this.selectedGroups.filter(g => g.id !== group.id);
        this.notifySuccess(this.$vs, `Acesso removido para o grupo ${group.display_name || group.name}`);

      } catch (error) {
        console.error('Erro ao remover compartilhamento com grupo:', error);
        this.notifyError(this.$vs, `Não foi possível remover o compartilhamento: ${error.message || 'Erro desconhecido'}`);
      } finally {
        this.sharingInProgress = false;
      }
    },
    async shareResource() {
      if (!this.resource) return;

      this.sharing = true;

      try {
        // Os compartilhamentos individuais já foram feitos
        // Este método é apenas para emitir o evento final com todos os dados
        const shareData = {
          resource: this.resource,
          users: this.selectedUsers,
          groups: this.selectedGroups,
          link: this.shareLink
        };

        // Emitir o evento para o componente pai
        this.$emit('shared', shareData);

        // Fechar o popup
        this.onClose();
      } catch (error) {
        console.error('Erro ao finalizar compartilhamento:', error);
        this.notifyError(this.$vs, 'Não foi possível concluir o compartilhamento.');
      } finally {
        this.sharing = false;
      }
    },
    // Método para carregar compartilhamentos existentes
    async loadExistingShares() {
      if (!this.resource) return;

      try {
        const response = await this.fileExplorerService.getResourceSharing({
          resource_id: this.resource.resource_id ?? '',
          media_id: this.resource.media_id ?? '',
          type: this.resource.type
        });

        if (response) {
          if (response.data.users && Array.isArray(response.data.users)) {
            this.selectedUsers = response.data.users;
          }

          if (response.data.groups && Array.isArray(response.data.groups)) {
            this.selectedGroups = response.data.groups;
          }

          if (response.data.link) {
            this.shareLink = response.data.link;
          }
        }
      } catch (error) {
        console.error('Erro ao carregar compartilhamentos existentes:', error);
        this.notifyError(this.$vs, 'Não foi possível carregar os compartilhamentos existentes.');
      }
    }
  },
  watch: {
    isVisible(val) {
      if (val && this.resource) {
        // Quando o popup é aberto, carregar compartilhamentos existentes
        this.loadExistingShares();
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.share-resource-container {
  padding: 1rem 0;
  width: 100%;
}

.resource-info {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;

  .icon-container {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background-color: rgba(var(--vs-primary), 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;

    .resource-icon {
      color: rgba(var(--vs-primary), 1);
    }
  }

  .resource-details {
    h4 {
      margin: 0;
      font-weight: 600;
      color: rgba(var(--vs-text), 1);
    }

    small {
      color: rgba(var(--vs-text), 0.6);
    }
  }
}

.share-link-section,
.share-users-section,
.share-groups-section {
  margin-bottom: 1.5rem;

  h5 {
    margin-top: 0;
    margin-bottom: 0.75rem;
    font-weight: 500;
    color: rgba(var(--vs-text), 1);
  }
}

.share-link-container {
  .link-wrapper {
    display: flex;
    align-items: center;
    background-color: rgba(var(--vs-primary), 0.05);
    border: 1px solid rgba(var(--vs-primary), 0.2);
    border-radius: 0.375rem;
    padding: 0.5rem;
    width: 100%;

    .share-link {
      flex: 1;
      color: rgba(var(--vs-primary), 1);
      text-decoration: none;
      word-break: break-all;
      padding-right: 0.5rem;
      font-size: 0.9rem;

      &:hover {
        text-decoration: underline;
      }
    }

    .no-link {
      flex: 1;
      color: rgba(var(--vs-text), 0.4);
      font-style: italic;
      font-size: 0.9rem;
    }

    .copy-button {
      margin-left: 0.5rem;
      flex-shrink: 0;
    }
  }
}

.selected-items-container {
  margin-top: 0.75rem;
  margin-bottom: 1.5rem;
}

.selected-items-heading {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: rgba(var(--vs-text), 0.7);

  small {
    margin-left: 0.5rem;
    opacity: 0.8;
  }
}

.selected-items-area {
  min-height: 80px;
  border: 1px dashed rgba(var(--vs-primary), 0.3);
  border-radius: 0.375rem;
  background-color: rgba(var(--vs-primary), 0.03);
  padding: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(var(--vs-primary), 0.5);
  }
}

.empty-selection {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 60px;
  color: rgba(var(--vs-text), 0.4);
  font-style: italic;
  font-size: 0.9rem;
}

.selected-users,
.selected-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .selected-user-tag,
  .selected-group-tag {
    display: flex;
    align-items: center;
    background-color: rgba(var(--vs-primary), 0.1);
    color: rgba(var(--vs-primary), 1);
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
    }

    .remove-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 0.5rem;
      cursor: pointer;
      color: #ef4444;
      transition: all 0.2s ease;
      opacity: 0.75;

      &:hover {
        transform: scale(1.15);
        opacity: 1;
        color: #dc2626;
      }

      &:active {
        transform: scale(0.95);
        color: #b91c1c;
      }
    }
  }
}

.selected-user-tag {
  &.is-owner {
    background-color: rgba(var(--vs-primary), 0.1);
    border-color: rgba(var(--vs-primary), 0.5);
    position: relative;
  }

  .owner-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    display: inline-block;
    background-color: rgba(var(--vs-primary), 0.2);
    color: rgba(var(--vs-primary), 1);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.7rem;
    z-index: 1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.user-option {
  display: flex;
  flex-direction: column;

  .user-name {
    font-weight: 500;
  }

  .user-email {
    font-size: 0.75rem;
    color: rgba(var(--vs-text), 0.6);
  }
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;

  .vs-button {
    margin-left: 0.5rem;
  }
}
</style>
